import { useMemo } from "react"

import { commentatorNeon } from "./commentator-neon"

const MOOD_HUE_MAP: Record<string, number> = {
  excited: 55,
  disgusted: 145,
  shocked: 250,
  smug: 310,
  furious: 28,
  amused: 80,
  neutral: 55,
}

function moodBg(mood: string | undefined): string {
  const h = MOOD_HUE_MAP[mood ?? "neutral"] ?? MOOD_HUE_MAP.neutral
  return `oklch(0.22 0.04 ${h} / 0.4)`
}

/** Generates a flat-top octagon SVG path — geometric doubled-line frame */
function octagonPath(cx: number, cy: number, radius: number): string {
  const pts: string[] = []
  for (let i = 0; i < 8; i++) {
    const angle = (Math.PI * 2 * i) / 8 - Math.PI / 2 + Math.PI / 8
    pts.push(
      `${cx + Math.cos(angle) * radius},${cy + Math.sin(angle) * radius}`,
    )
  }
  return `M ${pts[0]} L ${pts.slice(1).join(" L ")} Z`
}

interface CommentatorReactionProps {
  commentatorKey: string
  name: string
  mood: string | undefined
  line: string
  portraitUrl: string | null
  side: "left" | "right"
  opacity?: number
  translateX?: number
  rotate?: number
  scale?: number
}

const RING_SIZE = 80 // SVG viewbox for the ring (portrait is 64 centered in it)

export function CommentatorReaction({
  commentatorKey,
  name,
  mood,
  line,
  portraitUrl,
  side,
  opacity = 1,
  translateX = 0,
  rotate = 0,
  scale = 1,
}: CommentatorReactionProps) {
  const c = commentatorNeon(commentatorKey)

  // Triple geometric octagon frame — outer dimmest, middle mid, inner brightest
  const ringPaths = useMemo(() => {
    const center = RING_SIZE / 2
    return {
      inner: octagonPath(center, center, 34),
      mid: octagonPath(center, center, 37),
      outer: octagonPath(center, center, 40),
    }
  }, [])

  return (
    <div
      className="flex items-center"
      style={{
        opacity,
        transform: `translateX(${translateX}px) rotate(${rotate}deg) scale(${scale})`,
        willChange: "opacity, transform",
        transformOrigin: side === "left" ? "left center" : "right center",
        flexDirection: side === "right" ? "row-reverse" : "row",
      }}
    >
      {/* Portrait with geometric neon frame */}
      <div
        style={{
          position: "relative",
          zIndex: 2,
          flexShrink: 0,
          width: 64,
          height: 64,
        }}
      >
        {/* Doubled octagon neon frame */}
        <svg
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          style={{
            position: "absolute",
            top: (64 - RING_SIZE) / 2,
            left: (64 - RING_SIZE) / 2,
            width: RING_SIZE,
            height: RING_SIZE,
            pointerEvents: "none",
          }}
        >
          {/* Outer line — dimmest */}
          <path
            d={ringPaths.outer}
            fill="none"
            stroke={c.soft}
            strokeWidth={1}
            strokeLinejoin="miter"
            style={{
              filter: `drop-shadow(0 0 8px ${c.soft})`,
            }}
          />
          {/* Middle line */}
          <path
            d={ringPaths.mid}
            fill="none"
            stroke={c.glow}
            strokeWidth={1}
            strokeLinejoin="miter"
            style={{
              filter: `drop-shadow(0 0 6px ${c.glow}) drop-shadow(0 0 14px ${c.soft})`,
            }}
          />
          {/* Inner line — brightest */}
          <path
            d={ringPaths.inner}
            fill="none"
            stroke={c.neon}
            strokeWidth={1}
            strokeLinejoin="miter"
            style={{ filter: `drop-shadow(0 0 3px ${c.neon})` }}
          />
        </svg>

        {/* Clean portrait image */}
        <div
          className="overflow-hidden rounded-full"
          style={{ width: 64, height: 64 }}
        >
          {portraitUrl ? (
            <img
              src={portraitUrl}
              alt={`${name} ${mood ?? "neutral"}`}
              className="h-full w-full object-cover"
              style={side === "right" ? { transform: "scaleX(-1)" } : undefined}
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center text-2xl font-black"
              style={{ background: moodBg(mood), color: c.neon }}
            >
              {name.charAt(0)}
            </div>
          )}
        </div>
      </div>

      {/* Speech bubble — minimal dark glass, rounded on portrait side */}
      <div
        className="min-w-0 flex-1"
        style={{
          position: "relative",
          zIndex: 1,
          marginLeft: side === "left" ? -12 : undefined,
          marginRight: side === "right" ? -12 : undefined,
          background: "oklch(0.10 0.02 55 / 0.85)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          boxShadow: "0 4px 16px oklch(0 0 0 / 0.5)",
          clipPath:
            side === "left"
              ? "polygon(20px 0%, 100% 0%, 100% 100%, 20px 100%, 0% 50%)"
              : "polygon(0% 0%, calc(100% - 20px) 0%, 100% 50%, calc(100% - 20px) 100%, 0% 100%)",
        }}
      >
        <div
          className="py-3"
          style={{
            paddingLeft: side === "left" ? 28 : 16,
            paddingRight: side === "right" ? 28 : 16,
            textAlign: side === "right" ? "right" : "left",
          }}
        >
          <span
            className="mb-1 block text-xs font-black tracking-wider uppercase"
            style={{
              color: c.neon,
              textShadow: `0 0 8px ${c.glow}, 0 0 16px ${c.soft}`,
            }}
          >
            {name}
          </span>
          <p
            className="text-sm leading-relaxed text-[var(--foreground)]"
            style={{ textAlign: "left" }}
          >
            {line}
          </p>
        </div>
      </div>
    </div>
  )
}
